import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./StudyPlanner.css";
import {
  createCalendarEvent,
  createStudyPlan,
  createStudySession,
  deleteCalendarEvent,
  generateStudyPlan,
  getPlannerStats,
  getStudyPlan,
  listCalendarEvents,
  listStudySessions,
  updateCalendarEvent,
  updateStudyPlan,
} from "../../rtk/thunks/studyPlanner";
import { useNavigate } from "react-router-dom";

const getEventId = (event) => event?.id ?? event?._id ?? event?.event_id;
const getSessionId = (session) => session?.id ?? session?._id ?? session?.session_id;

const getEventTitle = (event) =>
  event?.title ||
  event?.name ||
  event?.course_title ||
  event?.course?.title ||
  "Study session";

const getEventDate = (event) =>
  event?.date || event?.event_date || event?.scheduled_date || event?.start_date || event?.start || event?.starts_at;

const getEventTime = (event) =>
  event?.start_time || event?.time || event?.scheduled_time || event?.starts_at || "";

const calendarColorOptions = [
  { value: "blue", label: "Blue" },
  { value: "red", label: "Red" },
];

const normalizeEventColor = (color) =>
  calendarColorOptions.some((option) => option.value === color) ? color : "blue";

const getEventColor = (event) => normalizeEventColor(event?.color);
const getEventReminderMinutes = (event) => event?.reminder_minutes_before ?? event?.reminder_minutes ?? event?.reminder_before_minutes ?? "";
const getEventRecurrence = (event) => event?.recurrence || "none";

const getSessionTitle = (session) =>
  session?.notes ||
  session?.title ||
  session?.name ||
  session?.topic ||
  session?.course_title ||
  session?.course?.title ||
  "Study session";

const getSessionDate = (session) =>
  session?.date || session?.session_date || session?.start_date || session?.started_at || session?.created_at;

const getSessionDuration = (session) =>
  session?.duration_minutes || session?.minutes || session?.planned_minutes || "";

const getSessionCompleted = () => true;

const getSessionStatusLabel = (session) => {
  if (getSessionCompleted(session)) return "Complete";
  const status = String(session?.status || session?.state || "").trim();
  if (!status) return "Pending";
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ");
};

const todayLocal = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};
const TOTAL_STUDY_HOURS = 65;

const calcStudyRecommendation = (examDate, startDate) => {
  if (!examDate) return null;
  const from = startDate && startDate > todayLocal() ? startDate : todayLocal();
  const start = new Date(`${from}T00:00:00`);
  const exam = new Date(`${examDate}T00:00:00`);
  const days = Math.round((exam - start) / 86400000);
  if (!(days > 0)) return null;
  const dailyHours = TOTAL_STUDY_HOURS / days;
  const weeklyHours = Math.min(TOTAL_STUDY_HOURS, dailyHours * 7);
  return {
    days,
    dailyMinutes: Math.round(dailyHours * 60),
    weeklyHours: Math.round(weeklyHours * 10) / 10,
  };
};

const formatMinutes = (m) =>
  m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ""}` : `${m} min`;
const toDateInputValue = (value) => {
  if (!value) return "";
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw.slice(0, 10)) && raw.length <= 10) return raw.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return raw.slice(0, 10);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const getNumber = (...values) => {
  const value = values.find((item) => item !== undefined && item !== null && item !== "");
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const SetupIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const GoalIcon = () => <img src="/assets/dashboardIcon.png" alt="Goal" width="48" height="48" />;
const TaskIcon = () => <img src="/assets/readScore.png" alt="Task" width="48" height="48" />;
const StreakIcon = () => <img src="/assets/3.png" alt="Streak" width="48" height="48" />;
const TimeIcon = () => <img src="/assets/4.png" alt="Time" width="48" height="48" />;
const Goalshape = () => <img src="/assets/studyplanner/goalshape.png" alt="" width="50" height="50" />;
const Taskshape = () => <img src="/assets/studyplanner/taskshape.png" alt="" width="50" height="50" />;
const Streakshape = () => <img src="/assets/studyplanner/streakshape.png" alt="" width="50" height="50" />;
const Timeshape = () => <img src="/assets/studyplanner/timeshape.png" alt="" width="50" height="50" />;

const PlannerFooter = ({ onSave, loading, saveLabel = "Save" }) => (
  <div className="planner-footer">
    <button className="planner-button" type="button">
      Cancel
    </button>
    <button className="planner-button planner-save-button" type="button" onClick={onSave} disabled={loading}>
      {loading ? "Saving..." : saveLabel}
    </button>
  </div>
);

const StudyPlannerForm = ({ onGenerate, loading }) => {
  const [endDate, setEndDate] = useState("");
  const [validationError, setValidationError] = useState("");
  const rec = useMemo(() => calcStudyRecommendation(endDate), [endDate]);

  const handleSave = () => {
    if (!endDate) {
      setValidationError("Pick your CPHQ exam date so we can calculate your schedule.");
      return;
    }
    if (!rec) {
      setValidationError("The exam date must be in the future.");
      return;
    }
    setValidationError("");
    onGenerate({
      exam_date: endDate,
      hours_per_week: rec.weeklyHours,
    });
  };

  return (
    <div className="study-planner-form-container">
      <div className="study-planner-form-header-row">
        <h2 className="study-planner-form-title">Smart Study Planner</h2>
        <div className="study-planner-form-header-actions">
          <button className="study-planner-action-button" type="button">
            <SetupIcon />
            Setup Study Period
          </button>
        </div>
      </div>
      <div className="study-planner-input-row">
        <div className="study-planner-input-group">
          <label className="study-planner-label">CPHQ Exam Date</label>
          <input className="study-planner-input" type="date" min={todayLocal()} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
        <div className="study-planner-input-group">
          <label className="study-planner-label">Recommended Hours Per Week</label>
          <input
          className="study-planner-input"
          type="text"
          readOnly
          tabIndex={-1}
          value={rec ? rec.weeklyHours : ""}
          placeholder="Calculated automatically"
          style={{
          pointerEvents: "none",
          caretColor: "transparent",
          userSelect: "none",
          cursor: "default",
          outline: "none",
           }}/>  
        </div>
      </div>
      {validationError && <p className="study-planner-error">{validationError}</p>}
      {rec ? (
        <div className="study-planner-ai-summary">
          <p>Total CPHQ study workload: {TOTAL_STUDY_HOURS} hours · {rec.days} days until your exam</p>
          <p>Recommended: {formatMinutes(rec.dailyMinutes)} per day · {rec.weeklyHours} hours per week</p>
        </div>
      ) : (
        <div className="study-planner-content-empty">
          <span className="study-planner-empty-icon">∅</span>
          <p className="study-planner-empty-text">
            Enter your CPHQ exam date. We will divide the 65 hours of study by the days remaining to
            calculate your daily and weekly study time.
          </p>
        </div>
      )}
      <PlannerFooter onSave={handleSave} loading={loading} saveLabel={loading ? "Generating your plan..." : "Generate My Study Plan"} />
    </div>
  );
};

const CalendarEventForm = ({ onCreate, loading }) => {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(toDateInputValue(new Date()));
  const [color, setColor] = useState("blue");
  const [reminderMinutesBefore, setReminderMinutesBefore] = useState("15");
  const [recurrence, setRecurrence] = useState("weekly");

  const handleSubmit = (event) => {
    event.preventDefault();
    onCreate({
      title,
      date,
      color,
      reminder_minutes_before: reminderMinutesBefore ? Number(reminderMinutesBefore) : 0,
      recurrence,
    }).then((success) => {
      if (success) {
        setTitle("");
        setColor("blue");
        setReminderMinutesBefore("15");
        setRecurrence("weekly");
      }
    });
  };

  return (
    <form className="calendar-event-form" onSubmit={handleSubmit}>
      <div className="study-planner-input-group">
        <label className="study-planner-label">Event Title</label>
        <input className="study-planner-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Course or topic" required />
      </div>
      <div className="study-planner-input-group">
        <label className="study-planner-label">Date</label>
        <input className="study-planner-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
      </div>
      <div className="study-planner-input-group">
        <label className="study-planner-label">Color</label>
        <select className="study-planner-input" value={color} onChange={(e) => setColor(e.target.value)}>
          {calendarColorOptions.map((option) => (
            <option value={option.value} key={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <div className="study-planner-input-group">
        <label className="study-planner-label">Reminder Before</label>
        <input className="study-planner-input" type="number" min="0" value={reminderMinutesBefore} onChange={(e) => setReminderMinutesBefore(e.target.value)} />
      </div>
      <div className="study-planner-input-group">
        <label className="study-planner-label">Recurrence</label>
        <select className="study-planner-input" value={recurrence} onChange={(e) => setRecurrence(e.target.value)}>
          <option value="none">None</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>
      <button className="planner-button planner-save-button calendar-event-submit" type="submit" disabled={loading}>
        {loading ? "Adding..." : "Add Event"}
      </button>
    </form>
  );
};

const CalendarEventEditor = ({ event, onUpdate, onCancel, loading }) => {
  const [title, setTitle] = useState(getEventTitle(event));
  const [color, setColor] = useState(getEventColor(event));

  const handleSubmit = (submitEvent) => {
    submitEvent.preventDefault();
    onUpdate(getEventId(event), {
      title,
      color,
    });
  };

  return (
    <form className="planner-inline-edit" onSubmit={handleSubmit}>
      <input className="study-planner-input" value={title} onChange={(e) => setTitle(e.target.value)} required />
      <select className="study-planner-input" value={color} onChange={(e) => setColor(e.target.value)}>
        {calendarColorOptions.map((option) => (
          <option value={option.value} key={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <div className="planner-inline-actions">
        <button className="planner-button planner-save-button" type="submit" disabled={loading}>
          Update
        </button>
        <button className="planner-button" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
};

const StudySessionForm = ({ onCreate, loading }) => {
  const [date, setDate] = useState(todayLocal);
  const [durationMinutes, setDurationMinutes] = useState("90");
  const [notes, setNotes] = useState("Reviewed chapters 1 to 3");

  const handleSubmit = (event) => {
    event.preventDefault();
    onCreate({
      date,
      duration_minutes: Number(durationMinutes),
      notes,
    }).then((success) => {
      if (success) {
        setDate(todayLocal());
        setDurationMinutes("90");
        setNotes("");
      }
    });
  };

  const navigate = useNavigate();
  return (
    <form className="calendar-event-form session-event-form" onSubmit={handleSubmit}>
      <div className="study-planner-input-group">
        <label className="study-planner-label">Date</label>
        <input className="study-planner-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
      </div>
      <div className="study-planner-input-group">
        <label className="study-planner-label">Duration Minutes</label>
        <input className="study-planner-input" type="number" min="1" value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value)} required />
      </div>
      <div className="study-planner-input-group">
        <label className="study-planner-label">Notes</label>
        <input className="study-planner-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Reviewed chapters 1 to 3" required />
      </div>
      <button className="planner-button planner-save-button calendar-event-submit" type="submit" disabled={loading}>
        {loading ? "Adding..." : "Add Session"}
      </button>
    </form>
  );
};

const CalendarComponent = ({ events, onCreateEvent, onDeleteEvent, loading }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("month");

  const eventsByDate = useMemo(() => {
    return events.reduce((acc, event) => {
      const date = toDateInputValue(getEventDate(event));
      if (!date) return acc;
      acc[date] = [...(acc[date] || []), event];
      return acc;
    }, {});
  }, [events]);

  const calendarGrid = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const mondayOffset = (firstOfMonth.getDay() + 6) % 7;
    const start = new Date(year, month, 1 - mondayOffset);
    const length = viewMode === "week" ? 7 : 42;

    return Array.from({ length }, (_, index) => {
      const day = new Date(start);
      day.setDate(start.getDate() + index);
      const dateKey = toDateInputValue(day);
      return {
        key: dateKey,
        day: day.getDate(),
        isCurrentMonth: day.getMonth() === month,
        isToday: dateKey === toDateInputValue(new Date()),
        events: eventsByDate[dateKey] || [],
      };
    });
  }, [currentDate, eventsByDate, viewMode]);

  const handleMonthChange = (offset) => {
    setCurrentDate((prevDate) => new Date(prevDate.getFullYear(), prevDate.getMonth() + offset, 1));
  };

  return (
    <div className="dashboard-card calendar-card">
      <div className="dashboard-calendar-header">
        <h3 className="calendar-month-year">
          {currentDate.toLocaleString("default", { month: "long", year: "numeric" })}
        </h3>
        <div className="dashboard-calendar-controls">
          <div className="view-toggle">
            <button className={`view-toggle-button ${viewMode === "week" ? "active" : ""}`} type="button" onClick={() => setViewMode("week")}>
              Week
            </button>
            <button className={`view-toggle-button ${viewMode === "month" ? "active" : ""}`} type="button" onClick={() => setViewMode("month")}>
              Month
            </button>
          </div>
          <div className="month-navigation">
            <button className="nav-button" type="button" onClick={() => handleMonthChange(-1)}>
              &lt;
            </button>
            <button className="today-button" type="button" onClick={() => setCurrentDate(new Date())}>
              Today
            </button>
            <button className="nav-button" type="button" onClick={() => handleMonthChange(1)}>
              &gt;
            </button>
          </div>
        </div>
      </div>

      <CalendarEventForm onCreate={onCreateEvent} loading={loading.createCalendarEvent} />

      <div className="dashboard-calendar-grid">
        {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((day) => (
          <div className="dashboard-calendar-day-header" key={day}>
            {day}
          </div>
        ))}
        {calendarGrid.map((item) => (
          <div key={item.key} className={`dashboard-calendar-day ${item.isToday ? "dashboard-day-active" : ""} ${!item.isCurrentMonth ? "other-month" : ""}`}>
            <span className="day-number">{item.day}</span>
            {item.events.map((event) => (
              <span className={`dashboard-calendar-tag ${getEventColor(event)}`} key={getEventId(event) || `${item.key}-${getEventTitle(event)}`}>
                {getEventTitle(event)}
                {getEventId(event) && (
                  <button className="calendar-event-delete" type="button" onClick={() => onDeleteEvent(getEventId(event))} aria-label={`Delete ${getEventTitle(event)}`}>
                    x
                  </button>
                )}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const PlannerDataLists = ({
  calendarEvents,
  studySessions,
  onDeleteEvent,
  onUpdateEvent,
  onCreateSession,
  loading,
}) => {
  const [editingEventId, setEditingEventId] = useState(null);

  const handleUpdateEvent = (eventId, payload) =>
    onUpdateEvent(eventId, payload).then((success) => {
      if (success) setEditingEventId(null);
    });

    const navigate = useNavigate();

  return (
    <div className="planner-data-grid">
      <div className="dashboard-card planner-list-card">
        <div className="planner-list-header">
          <h3 className="planner-list-title">Calendar Events</h3>
          <span className="planner-list-count">{calendarEvents.length}</span>
        </div>
        {calendarEvents.length ? (
          <div className="planner-list">
            {calendarEvents.map((event, index) => {
              const eventId = getEventId(event);
              const isEditing = eventId && String(editingEventId) === String(eventId);
              return (
                <div className="planner-list-row" key={eventId || `${getEventTitle(event)}-${index}`}>
                  {isEditing ? (
                    <CalendarEventEditor
                      event={event}
                      onUpdate={handleUpdateEvent}
                      onCancel={() => setEditingEventId(null)}
                      loading={loading}
                    />
                  ) : (
                    <>
                      <div className="planner-list-main">
                        <strong>{getEventTitle(event)}</strong>
                        <span>
                          {toDateInputValue(getEventDate(event)) || "No date"}
                          {getEventTime(event) ? ` at ${String(getEventTime(event)).slice(0, 5)}` : ""}
                        </span>
                        <span>
                          {getEventColor(event)}
                          {getEventReminderMinutes(event) !== "" ? ` - ${getEventReminderMinutes(event)} min before` : ""}
                          {getEventRecurrence(event) !== "none" ? ` - ${getEventRecurrence(event)}` : ""}
                        </span>
                      </div>
                      <div className="planner-list-actions">
                        <button
                          className="planner-button planner-compact-button"
                          type="button"
                          disabled={loading || !eventId}
                          onClick={() => setEditingEventId(eventId)}
                        >
                          Edit
                        </button>
                        <button
                          className="planner-delete-button"
                          type="button"
                          disabled={loading || !eventId}
                          onClick={() => onDeleteEvent(eventId)}
                        >
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="planner-empty-list">No calendar events found.</div>
        )}
      </div>

      <div className="dashboard-card planner-list-card">
        <div className="planner-list-header">
          <h3 className="planner-list-title">Study Sessions</h3>
          <span className="planner-list-count">{studySessions.length}</span>
        </div>
        <StudySessionForm onCreate={onCreateSession} loading={loading} />
        {studySessions.length ? (
          <div className="planner-list">
            {studySessions.map((session, index) => {
              const sessionId = getSessionId(session);
              return (
                <div className="planner-list-row" key={sessionId || `${getSessionTitle(session)}-${index}`}>
                  <div className="planner-list-main">
                    <strong>{getSessionTitle(session)}</strong>
                    <span>
                      {toDateInputValue(getSessionDate(session)) || "No date"}
                      {getSessionDuration(session) ? ` - ${getSessionDuration(session)} min` : ""}
                    </span>
                  </div>
                  <div className="planner-list-actions">
                    <span className={`planner-status-pill ${getSessionCompleted(session) ? "complete" : ""}`}>
                      {getSessionStatusLabel(session)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="planner-empty-list">No study sessions found.</div>
        )}
      </div>


      {/* ai recommendations */}
      <div className="dashboard-card">
        <h4 className="ai-recommendations">AI Recommendations</h4>
        <hr className="recommendations-divider" />
        <div className="dashboard-recommendation-card test">
          <span className="dashboard-rec-icon test">
            {/* <TestIcon /> */}
          </span>
          <div>
            <strong>Ready for Practice Test</strong>
            <p>
              Your Quality Management skills are strong. Take a full mock
              exam to gauge overall progress..
            </p>
            <button onClick={() => navigate('/question-bank')} className="dashboard-rec-button">Take Test</button>
          </div>
        </div>
        <div className="dashboard-recommendation-card momentum">
          <span className="dashboard-rec-icon momentum">
            {/* <MomentumIcon /> */}
          </span>
          <div>
            <strong>Maintain Study Momentum</strong>
            <p>Excellent 7-day streak! Keep up the daily sessions.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const StudyPlannerDashboard = ({ studyPlan, plannerStats, studySessions, onSave, onRegenerate, loading }) => {
  const [startDate, setStartDate] = useState(toDateInputValue(studyPlan?.start_date));
  const [endDate, setEndDate] = useState(toDateInputValue(studyPlan?.end_date));
  const [dailyGoalMinutes, setDailyGoalMinutes] = useState("");
  const [weeklyGoalHours, setWeeklyGoalHours] = useState("");
  const [formError, setFormError] = useState("");
  const rec = calcStudyRecommendation(endDate);
  const isChanged =
  startDate !== toDateInputValue(studyPlan?.start_date) ||
  endDate !== toDateInputValue(studyPlan?.end_date) ||
  Number(dailyGoalMinutes) !== getNumber(studyPlan?.daily_goal_minutes) ||
  Number(weeklyGoalHours) !== getNumber(studyPlan?.weekly_goal_hours);
  useEffect(() => {
    setStartDate(toDateInputValue(studyPlan?.start_date));
    setEndDate(toDateInputValue(studyPlan?.end_date));
    setDailyGoalMinutes(String(getNumber(studyPlan?.daily_goal_minutes, plannerStats?.daily_goal_minutes) || ""));
    setWeeklyGoalHours(String(getNumber(studyPlan?.weekly_goal_hours, plannerStats?.weekly_goal_hours) || ""));
  }, [plannerStats, studyPlan]);

  const dailyGoal = getNumber(dailyGoalMinutes, studyPlan?.daily_goal_minutes, plannerStats?.daily_goal_minutes);
  const weeklyGoal = getNumber(weeklyGoalHours, studyPlan?.weekly_goal_hours, plannerStats?.weekly_goal_hours);
  const totalSessions = getNumber(plannerStats?.total_sessions, studySessions.length);
  const completedSessions = getNumber(plannerStats?.completed_sessions, studySessions.filter((session) => getSessionCompleted(session)).length);
  const streak = getNumber(plannerStats?.streak_days, plannerStats?.current_streak);
  const studiedHours = getNumber(plannerStats?.total_hours_this_week, plannerStats?.studied_hours, plannerStats?.total_study_hours);
  const stats = [
    { icon: <GoalIcon />,value: `${rec ? rec.dailyMinutes : (dailyGoal || 0)}m`, label: (rec ? rec.weeklyHours : weeklyGoal) ? `of ${rec ? rec.weeklyHours : weeklyGoal}h Goal` : "Study hours",shape: <Goalshape /> },
    { icon: <TaskIcon />, value: `${completedSessions} of ${totalSessions}`, label: "Sessions", shape: <Taskshape /> },
    { icon: <StreakIcon />, value: streak, label: "Days in a Row", shape: <Streakshape /> },
    { icon: <TimeIcon />, value: `${dailyGoal || 0}m`, label: "Daily goal", shape: <Timeshape /> },
  ];

  const handleSave = () => {
    if (startDate && endDate && startDate > endDate) {
      setFormError("The start date cannot be after the end date.");
      return;
    }
    setFormError("");
    onSave({
      start_date: startDate,
      end_date: endDate,
      daily_goal_minutes: Number(dailyGoalMinutes),
      weekly_goal_hours: Number(weeklyGoalHours),
    });
  };

  return (
    <div>
      <div className="dashboard-stats-grid">
        {stats.map((stat) => (
          <div className="dashboard-stat-card" key={stat.label}>
            <span className="dashboard-stat-icon">{stat.icon}</span>
            <span className="dashboard-stat-shape">{stat.shape}</span>
            <div className="dashboard-stat-value">{stat.value}</div>
            <div className="dashboard-stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="dashboard-grid-two-column">
        <div className="dashboard-card">
          <div className="study-planner-form-header-row">
            <h2 className="study-planner-form-title">Smart Study Planner</h2>
            <div className="study-planner-form-header-actions">
              <button className="study-planner-action-button" type="button">
                <SetupIcon />
                Setup Study Period
              </button>
            </div>
          </div>
          <div className="study-planner-input-row">
            <div className="study-planner-input-group">
              <label className="study-planner-label">Start Date</label>
              <input className="study-planner-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="study-planner-input-group">
              <label className="study-planner-label">End Date</label>
              <input className="study-planner-input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <div className="study-planner-input-row">
            <div className="study-planner-input-group">
              <label className="study-planner-label">Daily Goal Minutes</label>
              <input className="study-planner-input" type="number" min="1" value={dailyGoalMinutes} onChange={(e) => setDailyGoalMinutes(e.target.value)} />
            </div>
            <div className="study-planner-input-group">
              <label className="study-planner-label">Weekly Goal Hours</label>
              <input className="study-planner-input" type="number" min="1" value={weeklyGoalHours} onChange={(e) => setWeeklyGoalHours(e.target.value)} />
            </div>
          </div>
          <div className="suggested-action-section" style={{ marginTop: "20px" }}>
            <h4 className="suggested-action-heading">Suggested Action</h4>
            <div className="suggested-action-item">
              <div className="suggested-action-content">
                <div className="suggested-action-label">Recommended Minutes per Day</div>
                <div className="suggested-action-value">{rec ? `${formatMinutes(rec.dailyMinutes)} daily` : `${dailyGoal || 60} minutes daily`}</div>
              </div>
            </div>
            <div className="suggested-action-item">
              <div className="suggested-action-content">
                <div className="suggested-action-label">Recommended Hours per Week</div>
                <div className="suggested-action-value">{rec ? `${rec.weeklyHours} hours weekly` : `${weeklyGoal || 7} hours weekly`}</div>
              </div>
            </div>
          </div>
          {formError && <p className="study-planner-error">{formError}</p>}
          <button
  className="planner-button"
  type="button"
  disabled={loading}
  onClick={() => {
    if (window.confirm("Rebuild your calendar from today? Your own events and sessions will stay.")) {
      onRegenerate({ exam_date: endDate });
    }
  }}
>
  Regenerate Plan
</button>
{isChanged && (  
  <PlannerFooter onSave={handleSave} loading={loading} saveLabel="Update Study Plan" />
)}
        </div>
      </div>

    </div>
  );
};

export default function StudyPlanner() {
  const dispatch = useDispatch();
  const { studyPlan, studySessions, plannerStats, calendarEvents, planSummary, planTips, loading, error } = useSelector((state) => state.studyPlanner);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    dispatch(getStudyPlan());
    dispatch(listStudySessions());
    dispatch(getPlannerStats());
    dispatch(listCalendarEvents());
  }, [dispatch]);

  const isPlannerGenerated = Boolean(studyPlan?.id || studyPlan?._id || studyPlan?.start_date || studyPlan?.end_date);

  const runAction = async (action) => {
    try {
      setLocalError("");
      await action();
      return true;
    } catch (apiError) {
      setLocalError(apiError?.error || apiError?.detail || apiError?.message || "Request failed.");
      return false;
    }
  };

  const handleCreateStudyPlan = (payload) =>
    runAction(async () => {
      await dispatch(createStudyPlan(payload)).unwrap();
      dispatch(getStudyPlan());
      dispatch(getPlannerStats());
      dispatch(listStudySessions());
    });

  const handleGenerateStudyPlan = (payload) =>
    runAction(async () => {
      await dispatch(generateStudyPlan(payload)).unwrap();
      dispatch(getStudyPlan());
      dispatch(getPlannerStats());
      dispatch(listStudySessions());
      dispatch(listCalendarEvents());
    });

  const handleUpdateStudyPlan = (payload) =>
    runAction(async () => {
      await dispatch(updateStudyPlan(payload)).unwrap();
      dispatch(getStudyPlan());
      dispatch(getPlannerStats());
      dispatch(listStudySessions());
    });

  const handleCreateCalendarEvent = (payload) =>
    runAction(async () => {
      await dispatch(createCalendarEvent(payload)).unwrap();
      dispatch(listCalendarEvents());
    });

  const handleDeleteCalendarEvent = (eventId) =>
    runAction(async () => {
      await dispatch(deleteCalendarEvent({ eventId })).unwrap();
      dispatch(listCalendarEvents());
    });

  const handleUpdateCalendarEvent = (eventId, payload) =>
    runAction(async () => {
      await dispatch(updateCalendarEvent({ eventId, calendarEventData: payload })).unwrap();
      dispatch(listCalendarEvents());
    });

  const handleCreateStudySession = (payload) =>
    runAction(async () => {
      await dispatch(createStudySession(payload)).unwrap();
      dispatch(listStudySessions());
      dispatch(getPlannerStats());
    });

  return (
    <div className="study-planner-page">
      <div className="study-planner-breadcrumb">
        <span>Home</span>
        <span>{">"}</span>
        <span>Study Planner</span>
      </div>
      {(localError || error) && <div className="study-planner-error">{localError || error}</div>}
      {isPlannerGenerated ? (
        <>
          {planSummary && (
            <div className="study-planner-ai-summary">
              <p>{planSummary}</p>
              {Array.isArray(planTips) && planTips.length > 0 && (
                <ul>
                  {planTips.map((tip, index) => (
                    <li key={index}>{tip}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <StudyPlannerDashboard
            studyPlan={studyPlan}
            studySessions={studySessions}
            plannerStats={plannerStats}
            onSave={handleUpdateStudyPlan}
            loading={loading.studyPlan}
            onRegenerate={handleGenerateStudyPlan}
          />
        </>
      ) : (
        <StudyPlannerForm onGenerate={handleGenerateStudyPlan} loading={loading.studyPlan} />
      )}
      <CalendarComponent events={calendarEvents} onCreateEvent={handleCreateCalendarEvent} onDeleteEvent={handleDeleteCalendarEvent} loading={loading} />
      <PlannerDataLists
        calendarEvents={calendarEvents}
        studySessions={studySessions}
        onDeleteEvent={handleDeleteCalendarEvent}
        onUpdateEvent={handleUpdateCalendarEvent}
        onCreateSession={handleCreateStudySession}
        loading={loading.createCalendarEvent} />
    </div>
  );
}
